import { useRef, useState } from "react";

import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { MessageSquare, Trash2, Edit3, ChevronDown } from "lucide-react";
import { dentalIcons } from "../../dentail-indetifcation/utils-icons/dentalIcons";
import moment from "moment";
import { Input, Tooltip } from "antd";

export function CommentPanel({
  comments,
  onAddComment,
  onEditComment,
  onDeleteComment,
  onResolveComment,
  selectedCommentId,
  onSelectComment,
  setShowCommentPanel,
  handleAddReply,
}) {
  const menuWrapRef = useRef(null);

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [showReplies, setShowReplies] = useState(false);
  const [openReplies, setOpenReplies] = useState({});

  const [comentMenuOpen, setComentMenuOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);

  const handleEdit = (comment) => {
    setEditingId(comment.id);
    setEditText(comment.text);
  };

  const handleSaveEdit = () => {
    if (editingId && editText.trim()) {
      onEditComment(editingId, editText.trim());
      setEditingId(null);
      setEditText("");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const startReply = (commentId) => {
    setReplyingId(commentId);
    setReplyText("");
  };

  const cancelReply = () => {
    setReplyingId(null);
    setReplyText("");
  };

  const sendReply = () => {
    if (replyingId && replyText.trim()) {
      handleAddReply?.(replyingId, replyText.trim());
      setReplyingId(null);
      setReplyText("");
    }
  };

  const activeComments = comments.filter((c) => !c.isResolved);
  const resolvedComments = comments.filter((c) => c.isResolved);

  return (
    <div className="w-full flex flex-col">
      {/* Header */}
      <div className="px-1 py-2 border-b border-gray-100 relative sticky top-0 z-[99] bg-white">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2 p-0 m-0">
              Comments
            </h3>
            <div className="bg-[#EDFAFF] text-[#0A9FD4] font-semibold w-6 h-6 text-center border border-[#D0F2FF] rounded-full">
              {comments.length}
            </div>
          </div>
          <div onClick={setShowCommentPanel} className="cursor-pointer">
            <img src={dentalIcons.backCalculator} className="h-6 w-6" />
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full">
          <div className="p-1 space-y-4">
            {/* Active Comments */}
            {activeComments.length > 0 && (
              <div className="space-y-3 mt-2">
                {activeComments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`bg-[#F5F7FA] rounded-[16px] cursor-pointer transition-all ${
                      selectedCommentId === comment.id
                        ? "ring-1 ring-blue-500 bg-[#20C3FF]"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => onSelectComment(comment.id)}
                  >
                    <div className="p-2">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex flex-col">
                          <span className="text-[16px] font-medium text-[#000000]">
                            {comment.author}
                          </span>
                          <span className="text-xs font-medium text-[#8C8C8C]">
                            {moment(comment.timestamp).fromNow()}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Tooltip title="Resolve">
                            <img
                              src={dentalIcons.resolveIcon}
                              alt="resolve"
                              onClick={(e) => {
                                e.stopPropagation();
                                onResolveComment(comment.id);
                              }}
                              className="cursor-pointer"
                            />
                          </Tooltip>
                          {editingId !== comment.id && (
                            <div className="relative" ref={menuWrapRef}>
                              <img
                                src={dentalIcons.dotsIcon}
                                alt="menu"
                                onClick={(e) => {
                                  e.stopPropagation(); // avoid selecting the card
                                  setSelectedComment(comment.id);
                                  setComentMenuOpen((open) =>
                                    comment.id === selectedComment
                                      ? !open
                                      : true
                                  );
                                }}
                              />
                              {comentMenuOpen &&
                                comment.id === selectedComment && (
                                  <div
                                    className="absolute top-6 right-0 w-[100px] p-[5px] bg-white rounded-[8px] border text-[#323538] font-medium"
                                    style={{
                                      boxShadow:
                                        "-2px -2px 16px 0 rgba(0, 0, 0, 0.08), 2px 2px 12px 0 rgba(0, 0, 0, 0.08)",
                                    }}
                                  >
                                    <div
                                      className="flex items-center gap-2 hover:bg-[#F2F4F7] p-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(comment);
                                        setComentMenuOpen(false);
                                      }}
                                    >
                                      <Edit3 className="h-4 w-4" /> Edit
                                    </div>
                                    <div
                                      className="flex items-center gap-2  hover:bg-[#F2F4F7] p-1 rounded-[8px] text-[#D4183D] font-medium"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteComment(comment.id);
                                        setComentMenuOpen(false);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />{" "}
                                      Delete
                                    </div>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </div>

                      {editingId === comment.id ? (
                        <div className="space-y-2">
                          <Input.TextArea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            autoSize={{ minRows: 2, maxRows: 10 }} // 👈 auto-resizing
                            placeholder="Edit comment..."
                            className="!rounded-[8px]"
                          />
                          <div className="flex gap-2 items-center justify-between ">
                            <div
                              onClick={handleCancelEdit}
                              className="bg-white border font-semibold h-[32px] w-[80px] text-[#8C8C8C] rounded-[7px] flex items-center justify-center text-[16px]"
                            >
                              Cancel
                            </div>
                            <div
                              onClick={handleSaveEdit}
                              className="bg-[#20C3FF] font-semibold h-[32px] w-[80px] text-white rounded-[7px] flex items-center justify-center text-[16px]"
                            >
                              Send
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-[#606060] font-medium mb-2">
                            {comment.text}
                          </p>
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className="text-xs underline text-[#535353] font-medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                startReply(comment.id);
                              }}
                            >
                              REPLY{" "}
                            </span>
                            {comment.replies && comment.replies.length > 0 && (
                              <div
                                className="flex items-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onSelectComment(comment.id);
                                  setOpenReplies((prev) => ({
                                    ...prev,
                                    [comment.id]: !prev[comment.id],
                                  }));
                                }}
                              >
                                <div className="text-xs text-[#8C8C8C] font-semibold">
                                  {comment.replies.length}{" "}
                                  {comment.replies.length === 1
                                    ? "Reply"
                                    : "Replies"}
                                </div>
                                <ChevronDown
                                  className={`h-5 w-5 transform transition-transform duration-200 ${
                                    openReplies[comment.id]
                                      ? "rotate-180"
                                      : "rotate-0"
                                  }`}
                                />
                              </div>
                            )}
                          </div>
                          {openReplies[comment.id] &&
                            comment.replies &&
                            comment.replies.length > 0 && (
                              <div className="ms-3 mt-1 mb-2">
                                {comment.replies.map((reply, index) => (
                                  <div
                                    className="bg-white px-2 py-1 rounded-[8px] mb-1"
                                    key={index}
                                  >
                                    <div className="flex flex-col">
                                      <span className="text-[14px] font-medium text-[#000000]">
                                        {comment.author}
                                      </span>
                                      <span className="text-[11px] font-medium text-[#8C8C8C]">
                                        {moment(comment.timestamp).fromNow()}
                                      </span>
                                    </div>
                                    <span className="text-[12px]">
                                      {reply.text}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}

                          {replyingId === comment.id && (
                            <div className="space-y-2 bg-white p-1 rounded-[8px]">
                              <Input.TextArea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                autoSize={{ minRows: 2, maxRows: 10 }} // 👈 auto-resizing
                                placeholder={`Reply @${comment.author}`}
                                className="!rounded-[8px]"
                              />
                              <div className="flex gap-2 items-center justify-between ">
                                <div
                                  onClick={cancelReply}
                                  className="bg-white border font-semibold h-[32px] w-[80px] text-[#8C8C8C] rounded-[7px] flex items-center justify-center text-[16px]"
                                >
                                  Cancel
                                </div>
                                <div
                                  onClick={sendReply}
                                  className="bg-[#EBF9FF] font-semibold h-[32px] w-[80px] text-[#20C3FF] rounded-[7px] flex items-center justify-center text-[16px]"
                                >
                                  Send
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Resolved Comments */}
            {resolvedComments.length > 0 && (
              <div className="space-y-3">
                {resolvedComments.map((comment) => (
                  <div
                    key={comment.id}
                    className="opacity-90 bg-[#F5F7FA] rounded-[16px]"
                  >
                    <div className="p-2">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[16px] font-medium text-[#000000]">
                              {comment.author}
                            </span>
                            <span className="text-xs font-medium text-[#8C8C8C]">
                              {moment(comment.timestamp).fromNow()}
                            </span>
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className="text-xs text-white"
                        >
                          Resolved
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {comment.text}
                      </p>

                      {comment.replies && comment.replies.length > 0 && (
                        <div className="text-xs text-blue-400 mt-1">
                          {comment.replies.length}{" "}
                          {comment.replies.length === 1 ? "reply" : "replies"}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {comments.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <div className="text-sm font-medium">No comments yet</div>
                <div className="text-xs mt-1">
                  Use the comment tool to add feedback
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
